import React, { useState, useRef, useEffect } from "react";

interface SwitcherEntity {
    properties: {
        value: string;
        icon?: string;
        label?: string;
    };
}

interface SimpleSwitcherProps {
    entities: SwitcherEntity[];
    value: string;
    onChange: (value: string) => void;
}

export const SimpleSwitcher: React.FC<SimpleSwitcherProps> = ({ entities, value, onChange }) => {
    const [activeDimension, setActiveDimension] = useState<{ left: string; width: string }>();
    const itemRefs = useRef<Record<string, HTMLDivElement>>({});

    useEffect(() => {
        const activeItemRef = itemRefs.current[value];
        if (activeItemRef) {
            setActiveDimension({
                left: `${activeItemRef.offsetLeft}px`,
                width: `${activeItemRef.getBoundingClientRect().width}px`
            });
        }
    }, [value, entities]);

    const handleItemClick = (newValue: string) => {
        if (value !== newValue) {
            onChange(newValue);
        }
    };

    return (
        <div className="tq-form-element un-ds-form-element un-ds-radio-switch-normal tq-no-label">
            <div className="tq-input-container tq-radio-switch" role="radiogroup">
                {value && activeDimension?.left && activeDimension?.width && (
                    <div
                        className={`tq-active ${
                            value === entities[0]?.properties?.value
                                ? "tq-has-selected-first"
                                : "tq-has-selected-last"
                        }`}
                        style={{
                            left: activeDimension.left,
                            width: activeDimension.width
                        }}
                    ></div>
                )}
                {entities.map(entity => {
                    const entityValue = entity.properties.value;
                    const active = value === entityValue;

                    return (
                        <div
                            key={entityValue}
                            ref={el => {
                                if (el) {
                                    itemRefs.current[entityValue] = el;
                                }
                            }}
                            className={`tq-radio-option ${active ? "tq-selected" : ""}`}
                            tabIndex={0}
                            onClick={() => handleItemClick(entityValue)}
                            onKeyDown={event => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    handleItemClick(entityValue);
                                }
                            }}
                            role="radio"
                            aria-checked={active}
                            aria-label={entity.properties.label || entity.properties.value}
                        >
                            <div className="tq-radio"></div>
                            {entity.properties.icon && <i className={entity.properties.icon}></i>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
